/**
 * Guards the post-login `?redirect=` target against open-redirect attacks.
 *
 * Only same-origin, root-relative paths are allowed. This rejects:
 * - absolute URLs (`https://evil.com`, `mailto:...`) — not root-relative
 * - protocol-relative URLs (`//evil.com`)
 * - backslash tricks (`/\evil.com`) — browsers normalize `\` to `/`, turning
 *   this into a protocol-relative URL
 * - the auth pages themselves (`/auth/signin...`) — would loop back to login
 */
export function isSafeInternalPath(value: unknown): value is string {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  if (value[0] !== '/') {
    return false;
  }
  // Reject `//...` and `/\...` (second char opening an authority component).
  if (value[1] === '/' || value[1] === '\\') {
    return false;
  }
  if (value.startsWith('/auth/signin')) {
    return false;
  }
  return true;
}

/**
 * Extracts a safe redirect path from a route-query `redirect` value, which may
 * be a string, a string array (repeated key), or absent. Returns the path when
 * it is a safe internal path, otherwise `null`.
 */
export function getSafeRedirectPath(raw: unknown): string | null {
  const target = Array.isArray(raw) ? raw[0] : raw;
  return isSafeInternalPath(target) ? target : null;
}
